import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

export interface InfiniteScrollProps {
  scrollRef: React.RefObject<HTMLElement>;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  offset?: number;
  disabled?: boolean;
  loadingDelay?: number;
  spinner?: React.ReactNode;
}

export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  offset = 200,
  disabled = false,
  loadingDelay = 300, // debounce time
  spinner = (
    <div className="flex w-full justify-center py-4">
      <Loader2 className="h-6 w-6 animate-spin text-neutral-500" />
    </div>
  ),
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled || !hasNextPage) return;

    const observerOptions = {
      root: null, // use the browser viewport
      rootMargin: `0px 0px ${offset}px 0px`,
      threshold: 0,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isFetchingNextPage) {
          setTimeout(() => {
            fetchNextPage();
          }, loadingDelay);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
      observer.disconnect();
    };
  }, [offset, disabled, hasNextPage, fetchNextPage, isFetchingNextPage, loadingDelay]);

  return (
    <>
      {isFetchingNextPage && spinner}
      <div ref={sentinelRef} />
    </>
  );
};